import { defineStore } from "pinia";
import { normalizeMode, normalizePlayer } from "@/utils/player";

/**
 * Текущая игра (pinia, только в памяти — переживает переходы между экранами, но не перезагрузку).
 *
 * state:
 *   uuid          — gameUUID или null (guard'ы /game, /result, /replay, /reward требуют uuid)
 *   role          — 'INITIATOR' | 'NOT_INITIATOR' | null
 *   mode          — 'BLINK' | 'MOUTH'
 *   opponent      — PlayerInfo соперника или null
 *   phase         — 'idle' | 'searching' | 'intro' | 'calibration' | 'playing' | 'processing' | 'final' | 'cancelled'
 *   result        — нормализованный результат (см. setResult) или null
 *   looserImgLink — ссылка на фото проигравшего или null
 *   reward        — звёзды, начисленные МНЕ за игру (40 победителю, 0 проигравшему)
 *   stars         — мой новый баланс после игры (или null, если неизвестен)
 *   endReason     — текст причины, если игра прервана (OPPONENT_LEFT, QUALITY_FAILED …)
 *   preview       — dev: значение ?preview=… (mockApi). Экран должен показать засеянное состояние
 *                   статически, без сети/камеры. В проде всегда null.
 *
 * getters: isInitiator, hasGame, hasResult
 * actions: startGame, setOpponent, setPhase, setResult, fillResult, setLooserImgLink, setEndReason, reset
 */

const initialState = () => ({
  uuid: null,
  role: null,
  mode: "BLINK",
  opponent: null,
  phase: "idle",
  result: null,
  looserImgLink: null,
  reward: 0,
  stars: null,
  endReason: null,
  preview: null,
  appliedResultGameUUID: null,
  postRoundNotice: "",
  postRoundSnapshot: null,
  privateRoom: false,
  roomId: null,
});

export const useGameStore = defineStore("game", {
  state: initialState,

  getters: {
    isInitiator: (state) => state.role === "INITIATOR",
    hasGame: (state) => !!state.uuid,
    hasResult: (state) => !!state.uuid && !!state.result,
  },

  actions: {
    /**
     * Начать игру по ответу create-game.
     * @param {{uuid:string, role:string, gameMode?:string}} created
     */
    startGame(created) {
      const preview = this.preview;
      this.$patch(initialState());
      this.preview = preview;
      this.uuid = created.uuid;
      this.role = created.role === "INITIATOR" ? "INITIATOR" : "NOT_INITIATOR";
      this.mode = normalizeMode(created.gameMode);
      this.privateRoom = created.privateRoom === true;
      this.roomId = created.roomId || null;
      this.phase = "searching";
    },

    /** Сохранить соперника (сырой PlayerInfo с сервера). */
    setOpponent(rawPlayer) {
      this.opponent = normalizePlayer(rawPlayer);
    },

    setPhase(phase) {
      this.phase = phase;
    },

    /**
     * Сохранить результат из FINAL-сообщения WS или из get-game-result.
     * Нормализованный result:
     *   { outcome:'WIN'|'LOSE'|null, winnerName, loserName, winnerUserId, loserUserId,
     *     looserImgLink, reward (мне), stars (мой баланс|null), gameMode, gameUUID }
     * @param {object} source  game_status FINAL или GameResult
     * @param {number|null} myUserId  userId текущего игрока (для GameResult без поля result)
     */
    setResult(source, myUserId = null) {
      if (!source) return;
      this.privateRoom = this.privateRoom || source.privateRoom === true;
      this.roomId = source.roomId || this.roomId;
      let outcome = source.result === "WIN" || source.result === "LOSE" ? source.result : null;
      const winnerUserId = source.winnerUserId ?? null;
      const loserUserId = source.loserUserId ?? null;
      if (!outcome && myUserId !== null && myUserId !== undefined) {
        if (winnerUserId !== null && Number(winnerUserId) === Number(myUserId)) outcome = "WIN";
        else if (loserUserId !== null && Number(loserUserId) === Number(myUserId)) outcome = "LOSE";
      }
      // В FINAL reward уже персональный; в GameResult — сумма для победителя.
      const personal = source.type === "game_status" || source.result !== undefined;
      const rawReward = Number(source.reward) || 0;
      const reward = this.privateRoom ? 0 : personal ? rawReward : outcome === "WIN" ? rawReward : 0;
      const stars = source.stars === undefined || source.stars === null ? null : Number(source.stars);
      const looserImgLink = source.looserImgLink || this.looserImgLink || null;
      this.result = {
        outcome,
        winnerName: source.winnerName || null,
        loserName: source.loserName || source.looserName || null,
        winnerUserId,
        loserUserId,
        looserImgLink,
        reward,
        stars,
        gameMode: normalizeMode(source.gameMode || this.mode),
        gameUUID: source.gameUUID || this.uuid,
        privateRoom: source.privateRoom === true || this.privateRoom,
        roomId: source.roomId || this.roomId,
      };
      this.looserImgLink = looserImgLink;
      this.reward = reward;
      this.stars = stars;
      this.phase = "final";
    },

    /**
     * Дополнить уже сохранённый результат полями из get-game-result (GameResult), если FINAL пришёл
     * неполным (например, ответ sync_game без итога). Уже известные поля не перезаписываются.
     * @param {object} source  GameResult ({status:'FINAL', winnerName, looserName, winnerUserId, …})
     * @param {number|null} myUserId
     */
    fillResult(source, myUserId = null) {
      if (!this.result || !source || source.status !== "FINAL") return;
      const current = this.result;
      const winnerUserId = current.winnerUserId ?? source.winnerUserId ?? null;
      const loserUserId = current.loserUserId ?? source.loserUserId ?? null;
      let outcome = current.outcome;
      if (!outcome && myUserId !== null && myUserId !== undefined) {
        if (winnerUserId !== null && Number(winnerUserId) === Number(myUserId)) outcome = "WIN";
        else if (loserUserId !== null && Number(loserUserId) === Number(myUserId)) outcome = "LOSE";
      }
      const looserImgLink = current.looserImgLink || source.looserImgLink || null;
      // Исход стал известен только сейчас: в GameResult reward — награда победителя.
      const reward = !this.privateRoom && !current.outcome && outcome === "WIN" && !current.reward ? Math.max(0, Number(source.reward) || 0) : current.reward;
      this.result = {
        ...current,
        outcome,
        reward,
        winnerName: current.winnerName || source.winnerName || null,
        loserName: current.loserName || source.loserName || source.looserName || null,
        winnerUserId,
        loserUserId,
        looserImgLink,
      };
      this.looserImgLink = looserImgLink;
      this.reward = reward;
    },

    setLooserImgLink(link) {
      this.looserImgLink = link || null;
      if (this.result) this.result = { ...this.result, looserImgLink: this.looserImgLink };
    },

    /** Игра прервана: phase='cancelled', endReason — текст для показа. */
    setEndReason(reason) {
      this.endReason = reason || null;
      this.phase = "cancelled";
    },

    /** Полный сброс (выход на главный экран). */
    reset() {
      this.$patch(initialState());
    },
  },
});
